import React, { useState, useEffect } from 'react'
import { X, Calendar, User, AlertTriangle, CheckCircle, FileText, Download, Image } from 'lucide-react'
import { supabase } from '../../../lib/supabase'

interface MachineDetailModalProps {
  machineId: string
  machineName: string
  machineHal: string
  isOpen: boolean
  onClose: () => void
}

interface Inspection {
  id: string
  created_at: string
  employee_name: string
  product_number: string
  current_weight: number
  control_weight: number
  meets_requirements: boolean
  comments: string
  pallet_check: boolean
  label_check: boolean
  sticker_check: boolean
  photo_url: string
}

interface Problem {
  id: string
  datum_tijd: string
  productcode: string
  gebruiker_naam: string
  argumentatie: string
  oplossing_gevonden: boolean
  oplossing_omschrijving: string
  fotos: string[]
}

export const MachineDetailModal: React.FC<MachineDetailModalProps> = ({
  machineId,
  machineName,
  machineHal,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'inspections' | 'problems'>('inspections')
  const [inspections, setInspections] = useState<Inspection[]>([])
  const [problems, setProblems] = useState<Problem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && machineId) {
      loadMachineData()
    }
  }, [isOpen, machineId])

  const loadMachineData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      console.log('🔄 Loading detailed data for machine:', machineId)

      // Load inspections for this machine
      const { data: inspectionsData, error: inspectionsError } = await supabase
        .from('quality_control')
        .select('*')
        .eq('machine_id', machineId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (inspectionsError) {
        console.error('❌ Error loading inspections:', inspectionsError)
      } else {
        setInspections(inspectionsData || [])
        console.log('✅ Inspections loaded:', inspectionsData?.length || 0)
      }

      // Load problems for this machine
      const { data: problemsData, error: problemsError } = await supabase
        .from('probleem_meldingen')
        .select('*')
        .eq('machine_id', machineId)
        .order('datum_tijd', { ascending: false })
        .limit(50)

      if (problemsError) {
        console.error('❌ Error loading problems:', problemsError)
      } else {
        setProblems(problemsData || [])
        console.log('✅ Problems loaded:', problemsData?.length || 0)
      }

    } catch (err) {
      console.error('❌ Error loading machine data:', err)
      setError('Fout bij het laden van machine gegevens')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const downloadPhoto = (photoData: string, filename: string) => {
    try {
      // Create a download link
      const link = document.createElement('a')
      link.href = photoData
      link.download = filename
      link.click()
    } catch (error) {
      console.error('Error downloading photo:', error)
      alert('Fout bij het downloaden van de foto')
    }
  }

  const PhotoViewer: React.FC<{ photos: string[], title: string }> = ({ photos, title }) => {
    const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null)

    if (!photos || photos.length === 0) return null

    return (
      <div className="mt-3">
        <span className="text-xs text-gray-500 block mb-2">Foto's ({photos.length}):</span>
        <div className="flex flex-wrap gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative">
              <img
                src={photo}
                alt={`${title} foto ${index + 1}`}
                className="w-16 h-16 object-cover border rounded cursor-pointer hover:opacity-75 transition-opacity"
                onClick={() => setSelectedPhoto(photo)}
              />
              <button
                onClick={() => downloadPhoto(photo, `${title}_foto_${index + 1}.jpg`)}
                className="absolute -top-2 -right-2 bg-blue-500 text-white p-1 rounded-full text-xs hover:bg-blue-600 transition-colors"
                title="Download foto"
              >
                <Download size={12} />
              </button>
            </div>
          ))}
        </div>

        {/* Photo Modal */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="max-w-4xl max-h-full">
              <img 
                src={selectedPhoto} 
                alt="Vergroot beeld"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="text-center mt-4">
                <button
                  onClick={() => setSelectedPhoto(null)}
                  className="bg-white text-black px-4 py-2 rounded mr-2 hover:bg-gray-100"
                >
                  Sluiten
                </button>
                <button
                  onClick={() => downloadPhoto(selectedPhoto, `${title}_foto.jpg`)}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  <Download size={16} className="inline mr-2" />
                  Download
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const getTodayInspections = () => {
    const today = new Date().toISOString().split('T')[0]
    return inspections.filter(inspection => 
      inspection.created_at.startsWith(today)
    ).length
  }

  const getOpenProblems = () => {
    return problems.filter(problem => !problem.oplossing_gevonden).length
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-kvh-red text-white p-3 sm:p-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold truncate">🔧 {machineName}</h2>
            <p className="text-red-200 text-sm">{machineHal}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition-colors min-h-touch min-w-touch flex items-center justify-center flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        {/* Statistics */}
        <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
            <div className="bg-blue-50 p-3 rounded">
              <div className="text-2xl font-bold text-blue-600">{getTodayInspections()}</div>
              <div className="text-sm text-gray-600">Inspecties vandaag</div>
            </div>
            <div className="bg-red-50 p-3 rounded">
              <div className="text-2xl font-bold text-red-600">{getOpenProblems()}</div>
              <div className="text-sm text-gray-600">Open problemen</div>
            </div>
            <div className="bg-green-50 p-3 rounded">
              <div className="text-2xl font-bold text-green-600">{inspections.length}</div>
              <div className="text-sm text-gray-600">Totaal inspecties</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex-shrink-0 overflow-x-auto">
          <nav className="flex space-x-4 sm:space-x-8 px-3 sm:px-4">
            <button
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-touch ${
                activeTab === 'inspections'
                  ? 'border-kvh-red text-kvh-red'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('inspections')}
            >
              <FileText size={16} className="inline mr-1 sm:mr-2" />
              Inspecties ({inspections.length})
            </button>
            <button
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-touch ${
                activeTab === 'problems'
                  ? 'border-kvh-red text-kvh-red'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('problems')}
            >
              <AlertTriangle size={16} className="inline mr-1 sm:mr-2" />
              Problemen ({problems.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-kvh-red"></div>
              <p className="mt-2 text-gray-600">Laden...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <AlertTriangle size={48} className="mx-auto mb-4" />
              <p>{error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'inspections' ? (
                inspections.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText size={48} className="mx-auto mb-4" />
                    <p>Geen inspecties gevonden voor deze machine</p>
                  </div>
                ) : (
                  inspections.map((inspection) => (
                    <div
                      key={inspection.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar size={16} className="mr-2" />
                          {formatDate(inspection.created_at)}
                        </div>
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            inspection.meets_requirements
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {inspection.meets_requirements ? 'Goedgekeurd' : 'Afgekeurd'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <span className="text-xs text-gray-500">Medewerker:</span>
                          <div className="font-medium">{inspection.employee_name}</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Product:</span>
                          <div className="font-medium">{inspection.product_number}</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Huidig gewicht:</span>
                          <div className="font-medium">{inspection.current_weight}g</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Controle gewicht:</span>
                          <div className="font-medium">{inspection.control_weight}g</div>
                        </div>
                      </div>

                      {inspection.comments && (
                        <div className="mb-3">
                          <span className="text-xs text-gray-500">Opmerkingen:</span>
                          <div className="text-sm">{inspection.comments}</div>
                        </div>
                      )}

                      <div className="flex gap-2 mb-3">
                        {inspection.pallet_check && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                            ✓ Pallet
                          </span>
                        )}
                        {inspection.label_check && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                            ✓ Label
                          </span>
                        )}
                        {inspection.sticker_check && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                            ✓ Sticker
                          </span>
                        )}
                      </div>

                      {/* Photo Display */}
                      {inspection.photo_url && (
                        <PhotoViewer 
                          photos={[inspection.photo_url]} 
                          title={`Inspectie_${inspection.id}`}
                        />
                      )}
                    </div>
                  ))
                )
              ) : (
                problems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <AlertTriangle size={48} className="mx-auto mb-4" />
                    <p>Geen problemen gevonden voor deze machine</p>
                  </div>
                ) : (
                  problems.map((problem) => (
                    <div
                      key={problem.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar size={16} className="mr-2" />
                          {formatDate(problem.datum_tijd)}
                        </div>
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            problem.oplossing_gevonden
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {problem.oplossing_gevonden ? 'Opgelost' : 'Open'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <span className="text-xs text-gray-500">Melder:</span>
                          <div className="font-medium">{problem.gebruiker_naam}</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Product:</span>
                          <div className="font-medium">{problem.productcode}</div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <span className="text-xs text-gray-500">Probleem:</span>
                        <div className="text-sm">{problem.argumentatie}</div>
                      </div>

                      {problem.oplossing_omschrijving && (
                        <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded mb-3">
                          <span className="text-xs text-green-600">Oplossing:</span>
                          <div className="text-sm text-green-800">{problem.oplossing_omschrijving}</div>
                        </div>
                      )}

                      {/* Photo Display */}
                      {problem.fotos && problem.fotos.length > 0 && (
                        <PhotoViewer 
                          photos={problem.fotos} 
                          title={`Probleem_${problem.id}`}
                        />
                      )}
                    </div>
                  ))
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}